import type { CartItem } from '@/components/account-cart-provider'
import type { FundingFormProgressRow } from '@/lib/application-progress-overrides'
import { resolveBaseProductId, type DonationFundingRow } from '@/lib/impact-donation'
import type { Product } from '@/lib/product-types'

export type ApplicationType = 'consumer' | 'express' | 'business'

export type AnalyticsProduct = {
  item_id: string
  item_name: string
  item_category: string
  item_variant?: string
  quantity: number
}

export type ProductRollup = {
  product_ids: string[]
  product_names: string[]
}

type FundingAnalyticsRow = Pick<
  DonationFundingRow,
  'accountId' | 'title' | 'type'
>

function accountTypeToAnalyticsProduct(
  accountType: CartItem['accountType'],
  instanceNumber: number,
  quantity: number,
): AnalyticsProduct {
  const baseId = resolveBaseProductId(accountType.id)
  const variant = quantity > 1 ? `${baseId}-${instanceNumber}` : undefined

  return {
    item_id: baseId,
    item_name: accountType.title,
    item_category: accountType.type,
    item_variant: variant,
    quantity: 1,
  }
}

export function cartItemsToAnalyticsProducts(cart: CartItem[]): AnalyticsProduct[] {
  const products: AnalyticsProduct[] = []

  for (const item of cart) {
    const quantity = Math.max(1, item.quantity || 1)
    for (let index = 0; index < quantity; index += 1) {
      products.push(accountTypeToAnalyticsProduct(item.accountType, index + 1, quantity))
    }
  }

  return products
}

export function fundingRowsToAnalyticsProducts(
  rows: Array<FundingFormProgressRow | FundingAnalyticsRow>,
): AnalyticsProduct[] {
  return rows.map((row) => {
    const baseId = resolveBaseProductId(row.accountId)

    return {
      item_id: baseId,
      item_name: row.title || baseId,
      item_category: row.type || 'checking',
      item_variant: row.accountId !== baseId ? row.accountId : undefined,
      quantity: 1,
    }
  })
}

export function selectedProductsToAnalyticsProducts(
  selections: Array<{
    id: string
    title?: string
    quantity?: number
    type?: string
  }>,
): AnalyticsProduct[] {
  const products: AnalyticsProduct[] = []

  for (const selection of selections) {
    const quantity = Math.max(1, Number(selection.quantity || 1))
    const baseId = resolveBaseProductId(selection.id)

    for (let index = 0; index < quantity; index += 1) {
      const variant = quantity > 1 ? `${baseId}-${index + 1}` : undefined
      products.push({
        item_id: baseId,
        item_name: selection.title || baseId,
        item_category: selection.type || 'business',
        item_variant: variant,
        quantity: 1,
      })
    }
  }

  return products
}

export function accountTypeToAnalyticsProductItem(
  accountType: CartItem['accountType'],
): AnalyticsProduct {
  return accountTypeToAnalyticsProduct(accountType, 1, 1)
}

export function rollupProductFlags(products: AnalyticsProduct[]): ProductRollup {
  const productsById = new Map<string, string>()

  for (const product of products) {
    if (!productsById.has(product.item_id)) {
      productsById.set(product.item_id, product.item_name)
    }
  }

  const entries = [...productsById.entries()]
  return {
    product_ids: entries.map(([itemId]) => itemId),
    product_names: entries.map(([, itemName]) => itemName),
  }
}

export function catalogProductToAnalyticsProduct(product: Product): AnalyticsProduct {
  return {
    item_id: product.id,
    item_name: product.accountDescription,
    item_category: product.accountType === 'CK'
      ? 'checking'
      : product.accountType === 'SV'
        ? 'savings'
        : 'cd',
    quantity: 1,
  }
}
